import { useBookingLocale } from '../../hooks/useBookingLocale';
import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Alert } from '../ui';
import { Button } from '../button';
import { useOutstandingPostTripInvoicesQuery } from '../../services/stripeService/__generated__/queries.generated';
import { formatChargeAmount, postTripChargeLabel } from './outstandingCharges';
import { OutstandingInvoiceRow, PayOutstandingInvoiceModal } from './PayOutstandingInvoiceModal';

const Rows = styled.div`
  ${tw`flex flex-col gap-3 mt-2`}
`;

const Row = styled.div`
  ${tw`flex flex-wrap items-center justify-between gap-2`}
`;

const RowText = styled.div`
  ${tw`flex flex-col min-w-0`}
`;

const RowTitle = styled.span`
  ${tw`text-sm font-semibold`}
  color: var(--color-text-primary);
`;

const RowMeta = styled.span`
  ${tw`text-xs`}
  color: var(--color-text-secondary);
`;

const Actions = styled.div`
  ${tw`flex items-center gap-3`}
`;

const Link = styled.a`
  ${tw`text-xs underline`}
  color: var(--color-text-secondary);
`;

/**
 * Spec 013 P5 (FR-023): unpaid post-trip charges on the guest's trip page, each with a
 * Pay button. Renders nothing when the guest owes nothing.
 */
export const OutstandingCharges: React.FC<{ bookingId: string; onPaid?: () => void }> = ({
  bookingId,
  onPaid,
}) => {
  // Spec 028: the trip's currency.
  const { currency } = useBookingLocale(bookingId, 'guest');
  const { data, refetch } = useOutstandingPostTripInvoicesQuery({
    variables: { id: bookingId },
    fetchPolicy: 'network-only',
  });
  const [paying, setPaying] = useState<OutstandingInvoiceRow | null>(null);
  const invoices = data?.getBookingAsGuest?.outstandingPostTripInvoices ?? [];
  if (invoices.length === 0) return null;

  return (
    <>
      <Alert tone="warning" title="Payment needed" data-testid="outstanding-charges">
        These charges for your trip could not be taken from your card. Pay them here with a card of
        this trip, a saved card or a new one.
        <Rows>
          {invoices.map(invoice => {
            const label = postTripChargeLabel(invoice.type);
            return (
              <Row key={invoice.id}>
                <RowText>
                  <RowTitle>
                    {label}: {formatChargeAmount(invoice.amount, currency)}
                  </RowTitle>
                  {invoice.description ? <RowMeta>{invoice.description}</RowMeta> : null}
                </RowText>
                <Actions>
                  {invoice.stripeInvoiceUrl ? (
                    <Link href={invoice.stripeInvoiceUrl} target="_blank" rel="noreferrer">
                      Payment link
                    </Link>
                  ) : null}
                  <Button
                    size="sm"
                    text="Pay"
                    onClick={() =>
                      setPaying({ id: invoice.id, amount: Number(invoice.amount), label })
                    }
                  />
                </Actions>
              </Row>
            );
          })}
        </Rows>
      </Alert>
      <PayOutstandingInvoiceModal
        bookingId={bookingId}
        invoice={paying}
        onClose={() => setPaying(null)}
        onPaid={() => {
          void refetch();
          onPaid?.();
        }}
      />
    </>
  );
};
